import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Table, Badge, Spinner, Alert,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import requests from "../requests";
import { buildViewFeaturedUrl } from "../utils/links";
import { useSeo } from "../hooks/useSeo";

const FREE_TIER_LIMIT = 3;

// ---------------------------------------------------------------------------
// Types — mirror build_featured_trends() output (FT.2) / GET /api/trends
// ---------------------------------------------------------------------------
interface TrendBase {
  ticker: string;
  ticker_name?: string | null;
  window: number;
  direction: string; // 'up' | 'down'
  rank?: number;
  conviction?: number;
  fraction?: number | null;
  count_total?: number | null;
}
interface Streak extends TrendBase { streak_days: number; }
interface Climber extends TrendBase { conviction_delta: number; prev_conviction: number; }
interface Confluence {
  ticker: string;
  ticker_name?: string | null;
  direction: string; // 'up' | 'down' | 'conflict'
  windows: number[];
  n_windows: number;
}
interface TrendsData {
  as_of?: string;
  lookback_days?: number;
  top_n?: number;
  streaks?: Streak[];
  new_entrants?: TrendBase[];
  climbers?: Climber[];
  confluence?: Confluence[];
  counts?: Record<string, number>;
}

// Featured window key (= window*step) → friendly preset label.
const WINDOW_LABEL: Record<number, string> = { 10: "2w", 20: "4w", 65: "3m", 130: "6m", 260: "1y" };
const winLabel = (w: number) => WINDOW_LABEL[w] ?? `w${w}`;

function DirBadge({ d }: { d: string }) {
  if (d === "up") return <Badge color="success">Bull</Badge>;
  if (d === "down") return <Badge color="danger">Bear</Badge>;
  return <Badge color="warning" text="dark">Conflict</Badge>;
}

function WinBadge({ w }: { w: number }) {
  return <Badge color="light" className="text-dark border me-1" style={{ fontWeight: 500 }}>{winLabel(w)}</Badge>;
}

// Window badge that links to /view-featured for this ticker/window/direction/date.
// Opens in a new tab so the trends list stays open for review.
function WinLink({ ticker, window, direction, date }:
  { ticker: string; window: number; direction: string; date?: string }) {
  return (
    <a
      href={buildViewFeaturedUrl({ direction, ticker, window, date })}
      target="_blank"
      rel="noreferrer"
      className="text-decoration-none"
      title="Open in View Featured"
    >
      <WinBadge w={window} />
    </a>
  );
}

const fmt = (n: number | null | undefined, digits = 2) =>
  n === null || n === undefined ? "—" : n.toFixed(digits);

// One tab's panel: a subtitle line + the section's table (or an empty state).
function Section({ subtitle, empty, children }:
  { subtitle: string; empty: boolean; children: React.ReactNode }) {
  return (
    <Card>
      <CardBody className="p-0">
        <div className="text-muted px-3 pt-3 pb-2" style={{ fontSize: 13 }}>{subtitle}</div>
        {empty ? (
          <div className="text-muted px-3 pb-3" style={{ fontSize: 13 }}>None today.</div>
        ) : children}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AdminTrends() {
  useSeo({ title: "Featured Trends", path: "/trends", noindex: true });
  const [data, setData] = useState<TrendsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("streaks");

  const authState = useSelector((state: RootReducerState) => state.authentication);
  const isPro = authState.user?.groups?.includes('pro') ?? false;
  const isLimited = !isPro; // both anonymous and free tier see a teaser only

  useEffect(() => {
    // Public endpoint (no auth) — fetch for every visitor; free/anon get a
    // truncated view below, gated client-side same as TopStocks/ClassicPatterns.
    requests.get("/api/trends")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setError("Could not load trends"); setLoading(false); });
  }, []);

  // Land on the first section that actually has trends.
  useEffect(() => {
    if (!data) return;
    const first = (["streaks", "new_entrants", "climbers", "confluence"] as const)
      .find(k => (data[k]?.length ?? 0) > 0);
    if (first) setActiveTab(first);
  }, [data]);

  const applyLimit = <T,>(rows: T[]) => isLimited ? rows.slice(0, FREE_TIER_LIMIT) : rows;

  const asOf = data?.as_of;
  const streaksAll = data?.streaks ?? [];
  const newEntrantsAll = data?.new_entrants ?? [];
  const climbersAll = data?.climbers ?? [];
  const confluenceAll = data?.confluence ?? [];
  const streaks = applyLimit(streaksAll);
  const newEntrants = applyLimit(newEntrantsAll);
  const climbers = applyLimit(climbersAll);
  const confluence = applyLimit(confluenceAll);
  const empty = !data || !data.as_of;
  const truncated = isLimited && (
    streaksAll.length > streaks.length ||
    newEntrantsAll.length > newEntrants.length ||
    climbersAll.length > climbers.length ||
    confluenceAll.length > confluence.length
  );

  const tickerCell = (t: { ticker: string; ticker_name?: string | null }) => (
    <td>
      <span style={{ fontWeight: 600 }}>{t.ticker}</span>
      {t.ticker_name && <span className="text-muted ms-2" style={{ fontSize: 12 }}>{t.ticker_name}</span>}
    </td>
  );

  return (
    <Container fluid className="py-4">
      <h2 className="mb-1" style={{ fontWeight: 700 }}>Featured Trends</h2>
      <p className="text-muted mb-4" style={{ fontSize: 14 }}>
        {empty
          ? "No trends yet — run the nightly matcher or the featured-history backfill first."
          : `As of ${data!.as_of} · last ${data!.lookback_days} days · top ${data!.top_n} per window`}
      </p>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : error ? (
        <Alert color="danger">{error}</Alert>
      ) : empty ? null : (
        <>
          <Nav tabs className="mb-3">
            {([
              { id: "streaks", label: "Streaks", count: streaksAll.length },
              { id: "new_entrants", label: "New entrants", count: newEntrantsAll.length },
              { id: "climbers", label: "Climbers", count: climbersAll.length },
              { id: "confluence", label: "Confluence", count: confluenceAll.length },
            ]).map(t => (
              <NavItem key={t.id}>
                <NavLink
                  active={activeTab === t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{ cursor: "pointer" }}
                >
                  {t.label}{" "}
                  <Badge color={activeTab === t.id ? "primary" : "secondary"} pill className="ms-1">
                    {t.count}
                  </Badge>
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="streaks">
              <Section subtitle="In the top list multiple days running" empty={!streaks.length}>
                <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
                  <thead><tr className="text-muted">
                    <th>Ticker</th><th>Window</th><th>Dir</th><th>Streak</th><th>Conviction</th><th>Rank</th><th>Matches</th>
                  </tr></thead>
                  <tbody>
                    {streaks.map((s, i) => (
                      <tr key={i}>
                        {tickerCell(s)}
                        <td><WinLink ticker={s.ticker} window={s.window} direction={s.direction} date={asOf} /></td>
                        <td><DirBadge d={s.direction} /></td>
                        <td><Badge color="primary">{s.streak_days}d</Badge></td>
                        <td>{fmt(s.conviction)}×</td>
                        <td className="text-muted">#{s.rank}</td>
                        <td className="text-muted">{s.count_total ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
            </TabPane>

            <TabPane tabId="new_entrants">
              <Section subtitle="In today's top list, absent across the lookback window" empty={!newEntrants.length}>
                <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
                  <thead><tr className="text-muted">
                    <th>Ticker</th><th>Window</th><th>Dir</th><th>Conviction</th><th>Rank</th><th>Matches</th>
                  </tr></thead>
                  <tbody>
                    {newEntrants.map((s, i) => (
                      <tr key={i}>
                        {tickerCell(s)}
                        <td><WinLink ticker={s.ticker} window={s.window} direction={s.direction} date={asOf} /></td>
                        <td><DirBadge d={s.direction} /></td>
                        <td>{fmt(s.conviction)}×</td>
                        <td className="text-muted">#{s.rank}</td>
                        <td className="text-muted">{s.count_total ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
            </TabPane>

            <TabPane tabId="climbers">
              <Section subtitle="Conviction rose materially vs the prior day" empty={!climbers.length}>
                <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
                  <thead><tr className="text-muted">
                    <th>Ticker</th><th>Window</th><th>Dir</th><th>Δ Conviction</th><th>Prev → Now</th><th>Rank</th>
                  </tr></thead>
                  <tbody>
                    {climbers.map((s, i) => (
                      <tr key={i}>
                        {tickerCell(s)}
                        <td><WinLink ticker={s.ticker} window={s.window} direction={s.direction} date={asOf} /></td>
                        <td><DirBadge d={s.direction} /></td>
                        <td><Badge color="info">+{fmt(s.conviction_delta)}</Badge></td>
                        <td className="text-muted">{fmt(s.prev_conviction)}× → {fmt(s.conviction)}×</td>
                        <td className="text-muted">#{s.rank}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
            </TabPane>

            <TabPane tabId="confluence">
              <Section subtitle="Same ticker featured across multiple windows today" empty={!confluence.length}>
                <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
                  <thead><tr className="text-muted">
                    <th>Ticker</th><th>Dir</th><th>Windows</th><th>#</th>
                  </tr></thead>
                  <tbody>
                    {confluence.map((s, i) => (
                      <tr key={i}>
                        {tickerCell(s)}
                        <td><DirBadge d={s.direction} /></td>
                        <td>{s.windows.map(w => (
                          <WinLink key={w} ticker={s.ticker} window={w} direction={s.direction} date={asOf} />
                        ))}</td>
                        <td><Badge color="warning" text="dark">{s.n_windows}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
            </TabPane>
          </TabContent>

          {truncated && (
            <div className="text-center py-3">
              <small className="text-muted">Showing top {FREE_TIER_LIMIT} per section. </small>
              <Link to="/pricing" className="small">Upgrade to Pro</Link>
              <small className="text-muted"> to see full trends.</small>
            </div>
          )}
        </>
      )}
    </Container>
  );
}
